import { writable } from 'svelte/store';

/**
 * Creates the terms store for managing patent term analysis results
 * 
 * The store uses an indexed key system where each term occurrence gets a unique key
 * like "parent_term_1", "parent_term_2", etc. This allows tracking multiple occurrences
 * of the same term text in different locations with different child terms.
 * 
 * @returns {Object} Store object with methods for term management
 */
export const createTermsStore = () => {
    const { subscribe, set, update } = writable({
        // Store indexed term entries directly from backend
        // Each entry has a unique key (e.g., "parent_term_1") mapping to full term data
        // This allows the same term text to appear multiple times with different contexts
        termEntries: new Map(), // Map<indexedKey, termData>
        
        // UI state tracked by indexed keys
        selectedEntries: new Set(), // Selected indexed keys
        highlightedEntries: new Set(), // Highlighted indexed keys
        editingEntry: null, // Currently being edited indexed key
        expandedEntries: new Set(), // Expanded indexed keys
        
        // Global UI state
        isTermAddingMode: false, // Whether the user is in term adding mode
        sortMode: 'appearance', // 'appearance' or 'alphabetical'
        isAnalyzingTerms: false, // Whether terms are currently being analyzed
        
        // Manual terms still tracked by term text (for now)
        manualTerms: new Set(), // Terms manually added by the user
        preservedTerms: new Set(), // Manual terms that were preserved during refresh (for visual feedback)
        
        // Operation tracking
        loadingOperations: new Map(), // Track all active loading operations
        fetchingChildTerms: new Set(), // Terms currently fetching children
        lastRefreshType: null, // Track the last refresh type from backend
        refreshStateSummary: null, // Track the backend state summary
        
        // Track if claims have been modified since last heavy refresh
        claimsModifiedSinceHeavyRefresh: false,
        
        // Claim dependencies from backend
        claimDependencies: {}, // Map of claim number to suggested dependencies (e.g., {"2": [1], "3": [1,2]})
        rawClaimRefs: {}, // Raw claim references before provisional/non-provisional logic applied
        
        // View filter state
        viewFilter: 'all', // 'all' | 'errors' | 'selected' | 'focus'
        focusedClaimNumber: null, // Cached claim number for focus mode
        lastClickedClaimNumber: null // Track last clicked claim
    });

    return {
        subscribe,
        set,
        update,
        // Note: addTerm() and removeTerm() methods removed as they're only used in tests
        // and are redundant with backend-first approach where updateTerms() handles all term changes
        // Toggle entry selection by indexed key
        toggleSelected: (indexedKey) => update(store => {
            console.log('Toggling entry selection:', indexedKey);
            const selectedEntries = store.selectedEntries || new Set();
            const newStore = {
                ...store,
                selectedEntries: selectedEntries.has(indexedKey)
                    ? new Set([...selectedEntries].filter(key => key !== indexedKey))
                    : new Set([...selectedEntries, indexedKey])
            };
            console.log('Store after toggling entry:', newStore);
            return newStore;
        }),
        // Add highlighted entry by indexed key
        addHighlight: (indexedKey) => update(store => ({
            ...store,
            highlightedEntries: new Set([...(store.highlightedEntries || new Set()), indexedKey])
        })),
        // Clear all highlights
        clearHighlights: () => update(store => ({
            ...store,
            highlightedEntries: new Set()
        })),
        // Start editing an entry by indexed key
        startEditing: (indexedKey) => update(store => ({
            ...store,
            editingEntry: indexedKey
        })),
        // Stop editing
        stopEditing: () => update(store => ({
            ...store,
            editingEntry: null
        })),
        // REMOVED: transferTermUIState - No longer needed with REPLACE approach
        
        // Toggle term adding mode
        toggleTermAddingMode: () => update(store => {
            const newMode = !store.isTermAddingMode;
            console.log('[termsStore] Toggling term adding mode:', store.isTermAddingMode, '->', newMode);
            return {
                ...store,
                isTermAddingMode: newMode
            };
        }),
        
        // Exit term adding mode
        exitTermAddingMode: () => update(store => {
            console.log('[termsStore] Exiting term adding mode, current mode:', store.isTermAddingMode);
            return {
                ...store,
                isTermAddingMode: false
            };
        }),
        
        // Add manually selected term (DEPRECATED - use PersistentTermsService instead)
        addManualTerm: (term) => update(store => {
            console.log('[termsStore] DEPRECATED: addManualTerm called. Please use PersistentTermsService.addManualTerm() instead');
            console.log('[termsStore] Adding manual term to tracking set only:', JSON.stringify(term));
            
            // Only track in manualTerms set for now
            if (term && term.trim()) {
                const trimmedTerm = term.trim();
                return {
                    ...store,
                    manualTerms: new Set([...(store.manualTerms || new Set()), trimmedTerm]),
                    isTermAddingMode: false // Exit term adding mode after selection
                };
            }
            
            return store;
        }),

        // Toggle expanded state for an entry by indexed key
        toggleExpanded: (indexedKey) => update(store => {
            console.log('Toggling expanded state for entry:', indexedKey);
            const newExpandedEntries = new Set(store.expandedEntries);
            
            if (newExpandedEntries.has(indexedKey)) {
                newExpandedEntries.delete(indexedKey);
            } else {
                newExpandedEntries.add(indexedKey);
            }
            
            return {
                ...store,
                expandedEntries: newExpandedEntries
            };
        }),
        
        // Note: Child terms are now stored within each termEntry, no separate methods needed
        
        // Set sort mode
        setSortMode: (mode) => update(store => {
            console.log('Setting sort mode:', mode);
            return {
                ...store,
                sortMode: mode
            };
        }),
        
        // Clear all selections
        clearAllSelections: () => update(store => {
            console.log('Clearing all selections');
            return {
                ...store,
                selectedEntries: new Set()
            };
        }),
        
        // Set highlighted entries by indexed keys
        setHighlighted: (indexedKeys) => update(store => {
            console.log('Setting highlighted entries:', indexedKeys);
            return {
                ...store,
                highlightedEntries: new Set(indexedKeys)
            };
        }),
        
        // Set term adding mode
        setTermAddingMode: (enabled) => update(store => {
            console.log('Setting term adding mode:', enabled);
            return {
                ...store,
                isTermAddingMode: enabled
            };
        }),
        
        // Set analyzing terms state
        setAnalyzingTerms: (isAnalyzing) => update(store => {
            console.log('Setting analyzing terms:', isAnalyzing);
            return {
                ...store,
                isAnalyzingTerms: isAnalyzing
            };
        }),
        
        // Clear preserved terms after animation
        clearPreservedTerms: () => update(store => ({
            ...store,
            preservedTerms: new Set()
        })),
        
        // Note: clearAutoDetectedTerms() method removed as it's unused
        // Backend now handles the distinction between manual and auto-detected terms
        
        // Set loading state for a specific operation
        setOperationLoading: (operationId, isLoading, metadata = {}) => update(store => {
            const operations = new Map(store.loadingOperations);
            
            if (isLoading) {
                operations.set(operationId, {
                    startTime: Date.now(),
                    ...metadata
                });
                console.log('[termsStore] Starting operation:', operationId, metadata);
            } else {
                operations.delete(operationId);
                console.log('[termsStore] Completed operation:', operationId);
            }
            
            return {
                ...store,
                loadingOperations: operations
            };
        }),
        
        // Set child term fetching state
        setFetchingChildTerms: (term, isFetching) => update(store => {
            const fetching = new Set(store.fetchingChildTerms);
            
            if (isFetching) {
                fetching.add(term);
                console.log('[termsStore] Fetching child terms for:', term);
            } else {
                fetching.delete(term);
                console.log('[termsStore] Completed fetching child terms for:', term);
            }
            
            return {
                ...store,
                fetchingChildTerms: fetching
            };
        }),
        
        // Set refresh state from backend
        setRefreshState: (refreshType, stateSummary) => update(store => {
            console.log('[termsStore] Setting refresh state:', refreshType, stateSummary);
            return {
                ...store,
                lastRefreshType: refreshType,
                refreshStateSummary: stateSummary,
                // Reset the modified flag when a heavy refresh occurs
                claimsModifiedSinceHeavyRefresh: refreshType === 'heavy' ? false : store.claimsModifiedSinceHeavyRefresh
            };
        }),
        
        // Mark that claims have been modified since last heavy refresh
        markClaimsModified: () => update(store => {
            return {
                ...store,
                claimsModifiedSinceHeavyRefresh: true
            };
        }),
        
        // Set claim dependencies from backend
        setClaimDependencies: (dependencies) => update(store => {
            console.log('[termsStore] Setting claim dependencies:', dependencies);
            return {
                ...store,
                claimDependencies: dependencies || {}
            };
        }),

        // Store raw claim references (before provisional/non-provisional logic)
        setRawClaimRefs: (rawRefs) => update(store => {
            console.log('[termsStore] Setting raw claim refs:', rawRefs);
            return {
                ...store,
                rawClaimRefs: rawRefs || {}
            };
        }),

        // Recalculate claim dependencies based on patent type
        // isProvisional: true = keep all refs, false = only keep highest claim number
        recalculateClaimDependencies: (isProvisional) => update(store => {
            console.log('[termsStore] Recalculating claim dependencies, isProvisional:', isProvisional);
            const rawRefs = store.rawClaimRefs;

            if (!rawRefs || Object.keys(rawRefs).length === 0) {
                console.log('[termsStore] No raw claim refs to recalculate');
                return store;
            }

            const dependencies = {};
            Object.entries(rawRefs).forEach(([claimNum, refs]) => {
                if (refs && refs.length > 0) {
                    if (isProvisional) {
                        // Provisional: keep all references
                        dependencies[claimNum] = refs;
                    } else {
                        // Non-provisional: only keep the highest claim number
                        dependencies[claimNum] = [Math.max(...refs)];
                    }
                }
            });

            console.log('[termsStore] Recalculated dependencies:', dependencies);
            return {
                ...store,
                claimDependencies: dependencies
            };
        }),

        // Set view filter mode
        setViewFilter: (filter) => update(store => {
            console.log('[termsStore] Setting view filter:', filter);
            return {
                ...store,
                viewFilter: filter
            };
        }),
        
        // Set focused claim for focus mode
        setFocusedClaim: (claimNumber) => update(store => {
            console.log('[termsStore] Setting focused claim:', claimNumber);
            return {
                ...store,
                focusedClaimNumber: claimNumber,
                lastClickedClaimNumber: claimNumber
                // Don't auto-switch to focus mode - let user control this via dropdown
            };
        }),
        
        // Reset store to initial state
        reset: () => set({
            termEntries: new Map(),
            selectedEntries: new Set(),
            highlightedEntries: new Set(),
            editingEntry: null,
            isTermAddingMode: false,
            expandedEntries: new Set(),
            sortMode: 'appearance',
            isAnalyzingTerms: false,
            manualTerms: new Set(),
            preservedTerms: new Set(),
            loadingOperations: new Map(),
            fetchingChildTerms: new Set(),
            lastRefreshType: null,
            refreshStateSummary: null,
            claimDependencies: {},
            rawClaimRefs: {},
            claimsModifiedSinceHeavyRefresh: false,
            viewFilter: 'all',
            focusedClaimNumber: null,
            lastClickedClaimNumber: null
        }),
        
        // Helper methods for PersistentTermsService
        
        // Add temporary term for immediate UI feedback while backend processes
        addTemporaryTerm: (termName, claimsText = null) => update(store => {
            console.log(`[termsStore] Adding temporary term: ${termName}`);
            const tempKey = `temp_${Date.now()}`;
            const newTermEntries = new Map(store.termEntries);
            
            // Calculate position for proper sorting if claims text is provided
            let claim_num = null;
            let start_char = null;
            let occurrence_order = 0; // Default to beginning if not found
            
            if (claimsText) {
                // Find first occurrence of the term in claims text using simple approach
                const termRegex = new RegExp(`\\b${termName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
                const termMatch = termRegex.exec(claimsText);
                
                if (termMatch) {
                    // Found the term - calculate its position for sorting
                    const absolutePosition = termMatch.index;
                    occurrence_order = absolutePosition;
                    
                    // Try to find which claim it belongs to by parsing the text before it
                    const textBeforeTerm = claimsText.substring(0, absolutePosition);
                    const claimMatches = Array.from(textBeforeTerm.matchAll(/(\d+)\./g));
                    if (claimMatches.length > 0) {
                        const lastClaimMatch = claimMatches[claimMatches.length - 1];
                        claim_num = parseInt(lastClaimMatch[1]);
                        // Calculate position within that claim
                        start_char = absolutePosition - lastClaimMatch.index - lastClaimMatch[0].length;
                    }
                    
                    console.log(`[termsStore] Found temporary term "${termName}" at absolute position ${absolutePosition}, claim ${claim_num}, relative position ${start_char}`);
                } else {
                    // Term not found - place it at the end based on existing terms
                    const existingTerms = Array.from(store.termEntries.values())
                        .filter(term => !term.temporary && term.occurrence_order != null)
                        .sort((a, b) => (a.occurrence_order || 0) - (b.occurrence_order || 0));
                    
                    if (existingTerms.length > 0) {
                        // Place it after the last term
                        occurrence_order = (existingTerms[existingTerms.length - 1].occurrence_order || 0) + 100;
                    } else {
                        // No existing terms, place at end of text
                        occurrence_order = claimsText.length;
                    }
                    console.log(`[termsStore] Term "${termName}" not found in claims, using fallback position ${occurrence_order}`);
                }
            }
            
            // Create temporary term entry with loading state
            newTermEntries.set(tempKey, {
                parent_term: termName,
                claim_num: claim_num,
                start_char: start_char,
                end_char: start_char ? start_char + termName.length : null,
                occurrence_order: occurrence_order,
                analyzed_for_children: false,
                parent_references: {
                    claims: [],
                    start_chars: [],
                    end_chars: []
                },
                children: {},
                warnings: [],
                temporary: true,
                user_generated: true,
                loading: true,  // Add loading indicator
                loadingMessage: 'Adding term...'  // Status message
            });
            
            return {
                ...store,
                termEntries: newTermEntries
            };
        }),
        
        // REMOVED: removeTemporaryTerm - Optimistic updates no longer needed
        // REMOVED: hideAllInstancesOfTerm and showAllInstancesOfTerm - No optimistic updates needed
        
        // REMOVED: previewTextModification and revertTextModification - No longer needed with REPLACE approach
        
        applyBackendResult: (result) => update(store => {
            console.log('[termsStore] Applying backend result:', result);
            
            if (result.term_counts) {
                // Convert backend term_counts to our termEntries format
                const newTermEntries = new Map();
                Object.entries(result.term_counts).forEach(([indexedKey, termData]) => {
                    // Remove any refresh-related and UI state flags since this is fresh data
                    if (termData.needs_refresh) {
                        delete termData.needs_refresh;
                    }
                    if (termData.recently_modified) {
                        delete termData.recently_modified;
                    }
                    // Remove UI-specific updating flags that backend doesn't know about
                    if (termData.updating) {
                        delete termData.updating;
                    }
                    if (termData.updating_from) {
                        delete termData.updating_from;
                    }
                    newTermEntries.set(indexedKey, termData);
                });
                
                return {
                    ...store,
                    termEntries: newTermEntries,
                    isAnalyzingTerms: false,
                    lastRefreshType: result.refresh_type || store.lastRefreshType,
                    refreshStateSummary: result.state_summary || store.refreshStateSummary
                };
            }
            
            return store;
        }),
        
        // Clear all terms and reset store to initial state (for importing new documents)
        clearAllTerms: () => update(_store => {
            console.log('[termsStore] Clearing all terms for new document import');
            return {
                termEntries: new Map(),
                selectedEntries: new Set(),
                highlightedEntries: new Set(),
                editingEntry: null,
                expandedEntries: new Set(),
                isTermAddingMode: false,
                sortMode: 'appearance',
                isAnalyzingTerms: false,
                manualTerms: new Set(),
                preservedTerms: new Set(),
                loadingOperations: new Map(),
                fetchingChildTerms: new Set(),
                lastRefreshType: null,
                refreshStateSummary: null,
                claimsModifiedSinceHeavyRefresh: false,
                claimDependencies: {},
                viewFilter: 'all',
                focusedClaimNumber: null,
                lastClickedClaimNumber: null
            };
        }),
        
        // REMOVED: applyBackendResultWithRefreshNeeded - No longer needed with REPLACE approach
        // The standard applyBackendResult handles all updates now
    };
};

export const termsStore = createTermsStore();

// Derived stores for automatic reactive sorting
// This eliminates the need for manual sorting in TermsPanel.svelte

/**
 * Get term positions in claims content for sorting
 * @param {Array} termsArray - Array of terms
 * @param {string} claimsContent - Claims content to search in
 * @returns {Object} - Object mapping terms to their first position
 */
function getTermPositions(termsArray, claimsContent) {
    const positions = {};
    
    // For each term, find its first occurrence in claims
    termsArray.forEach(term => {
        // Create regex that matches the term with word boundaries
        const escapeRegExp = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const termRegex = new RegExp(`\\b${escapeRegExp(term)}\\b`, 'i');
        
        // Find the position of the first match
        const match = termRegex.exec(claimsContent);
        positions[term] = match ? match.index : Infinity; // Use Infinity for terms not found
    });
    
    return positions;
}

/**
 * Sort terms by their first appearance in claims
 * @param {Array} termsArray - Array of terms to sort
 * @param {string} claimsContent - Claims content to search in
 * @returns {Array} - Sorted array of terms
 */
function sortTermsByFirstAppearance(termsArray, claimsContent) {
    if (!claimsContent) return termsArray;
    
    const positions = getTermPositions(termsArray, claimsContent);
    
    console.log('Term positions in claims:', positions);
    
    const sortedTerms = termsArray.sort((a, b) => {
        return positions[a] - positions[b];
    });
    
    console.log('Terms sorted by first appearance:', sortedTerms);
    
    return sortedTerms;
}

/**
 * Sort terms alphabetically
 * @param {Array} termsArray - Array of terms to sort
 * @returns {Array} - Alphabetically sorted array of terms
 */
function sortTermsAlphabetically(termsArray) {
    return [...termsArray].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
}

// Export functions for manual sorting (fallback)
export { sortTermsByFirstAppearance, sortTermsAlphabetically };

// Note: The reactive derived store for sorted terms is created in a separate file
// to avoid circular dependencies with editorsStore.js
// See: src/stores/derivedStores.js

/**
 * Get all terms (parent and children) from selected term entries
 * When a parent term entry is selected, this includes all its child terms in the result
 * @param {Set} selectedEntries - Set of selected indexed keys (e.g., 'parent_term_1', 'parent_term_2')
 * @param {Map} termEntries - Map of indexed keys to term data objects
 * @returns {Set} - Set of term strings including selected parent terms and all their children
 */
export function getSelectedTermsWithChildren(selectedEntries, termEntries) {
    const expanded = new Set();
    
    selectedEntries.forEach(indexedKey => {
        const entry = termEntries.get(indexedKey);
        if (entry) {
            // Add the parent term
            expanded.add(entry.parent_term);
            
            // Add all child terms for this entry
            if (entry.children && typeof entry.children === 'object') {
                Object.keys(entry.children).forEach(childTerm => {
                    expanded.add(childTerm);
                });
            }
        }
    });
    
    return expanded;
}

// Export old name for backward compatibility
export const getExpandedSelectedTerms = getSelectedTermsWithChildren;

